import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";
import { useCart } from "@/context/CartContext";
import type { Product } from "@/types/product";

// ─── Curated "also bought" picks by category ────────────────────────────────
const REASON_MAP: Record<string, string> = {
  solar: "Top-rated solar & battery system",
  gas: "High-output portable generator",
  "whole-home": "Whole-home standby protection",
};

function getTopPicks(allProducts: Product[]): Product[] {
  const categories: Product["category"][] = ["solar", "gas", "whole-home"];
  const picks: Product[] = [];
  for (const cat of categories) {
    const top = allProducts
      .filter((p) => p.category === cat && p.inStock)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 2);
    picks.push(...top);
  }
  // Deduplicate and cap at 6
  const seen = new Set<string>();
  return picks.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  }).slice(0, 6);
}

// ─── Shared mini product card ────────────────────────────────────────────────
function ProductMiniCard({
  product,
  reasonLabel,
}: {
  product: Product;
  reasonLabel?: string;
}) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;
  const savePct = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.inStock) return;
    addItem({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  const categoryBadge =
    product.category === "solar"
      ? { label: "Solar & Battery", color: "bg-sky-100 text-sky-700" }
      : product.category === "gas"
      ? { label: "Gas Generator", color: "bg-orange-100 text-orange-700" }
      : { label: "Whole-Home", color: "bg-emerald-100 text-emerald-700" };

  return (
    <div
      className="shrink-0 w-52 bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-amber-300 transition-all duration-200 cursor-pointer group flex flex-col"
      onClick={() => {
        navigate(`/shop/${product.id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
    >
      {/* Image */}
      <div className="relative w-full h-36 bg-gray-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
        />
        {saveDollars && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            Save ${saveDollars.toLocaleString()}
          </span>
        )}
        <span className={`absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${categoryBadge.color}`}>
          {categoryBadge.label}
        </span>
        {!product.inStock && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="text-xs font-bold text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded-md">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col flex-1">
        <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-0.5 truncate">
          {product.brand}
        </p>
        <h4 className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 mb-1.5 flex-1">
          {product.name}
        </h4>

        {/* Reason chip */}
        {reasonLabel && (
          <div className="flex items-start gap-1 mb-2">
            <div className="w-3 h-3 flex items-center justify-center mt-px shrink-0">
              <i className="ri-user-heart-line text-amber-500 text-[10px]"></i>
            </div>
            <p className="text-[10px] text-gray-400 leading-snug">{reasonLabel}</p>
          </div>
        )}

        {/* Stars */}
        <div className="flex items-center gap-0.5 mb-1.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <i
              key={s}
              className={`text-[10px] ${s <= Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}
            ></i>
          ))}
          <span className="text-[10px] text-gray-400 ml-0.5">({product.rating})</span>
        </div>

        {/* Price row */}
        <div className="flex items-center justify-between gap-1 mt-auto pt-2">
          <div className="flex items-baseline gap-1.5">
            <span className={`font-extrabold text-sm ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
              ${product.price.toLocaleString()}
            </span>
            {savePct && (
              <span className="text-[10px] text-red-500 font-semibold">{savePct}% off</span>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!product.inStock}
            className={`shrink-0 px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-all whitespace-nowrap cursor-pointer ${
              added
                ? "bg-emerald-500 text-white"
                : product.inStock
                ? "bg-amber-500 hover:bg-amber-400 text-white"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {added ? (
              <span className="flex items-center gap-0.5">
                <i className="ri-check-line"></i> Done
              </span>
            ) : product.inStock ? (
              <span className="flex items-center gap-0.5">
                <i className="ri-shopping-cart-2-line"></i> Add
              </span>
            ) : "—"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
type Tab = "recent" | "popular";

export default function GuidesCrossSell() {
  const [activeTab, setActiveTab] = useState<Tab>("recent");
  const scrollRef = useRef<HTMLDivElement>(null);

  const recentIds = useRecentlyViewed();
  const { products, loading } = useShopifyProducts();

  const recentProducts = useMemo(
    () =>
      recentIds
        .map((id) => products.find((p) => p.id === id))
        .filter((p): p is Product => Boolean(p))
        .slice(0, 8),
    [recentIds, products]
  );

  const popularPicks = useMemo(() => getTopPicks(products), [products]);

  const hasRecent = recentProducts.length > 0;
  const displayTab: Tab = hasRecent ? activeTab : "popular";
  const displayProducts = displayTab === "recent" ? recentProducts : popularPicks;

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "right" ? 230 : -230, behavior: "smooth" });
  };

  if (loading || displayProducts.length === 0) return null;

  return (
    <section className="mt-12 pt-10 border-t border-gray-100">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-50 border border-amber-200 rounded-xl">
            <i className="ri-shopping-bag-3-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-gray-900 leading-tight">
              {displayTab === "recent" ? "Pick Up Where You Left Off" : "Customers Also Bought"}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {displayTab === "recent"
                ? "Products you browsed — ready to add to cart"
                : "Top-rated systems across every category"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Tab switcher — only show if user has recent history */}
          {hasRecent && (
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                type="button"
                onClick={() => setActiveTab("recent")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "recent"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-history-line text-sm"></i>
                </span>
                Recently Viewed
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("popular")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "popular"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-fire-line text-sm"></i>
                </span>
                Popular Picks
              </button>
            </div>
          )}

          {/* Scroll arrows */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => scroll("left")}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable product row */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-3 scroll-smooth"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {displayProducts.map((product) => (
          <ProductMiniCard
            key={product.id}
            product={product}
            reasonLabel={
              displayTab === "popular" ? REASON_MAP[product.category] : undefined
            }
          />
        ))}
      </div>

      {/* Footer strip */}
      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-truck-line text-emerald-500 text-xs"></i>
            </span>
            Free shipping on all orders
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line text-amber-500 text-xs"></i>
            </span>
            Manufacturer warranty included
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-refresh-line text-gray-400 text-xs"></i>
            </span>
            30-day returns
          </span>
        </div>
        <Link
          to="/shop"
          className="flex items-center gap-1.5 text-xs font-bold text-amber-600 hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          Browse all products
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs"></i>
          </span>
        </Link>
      </div>
    </section>
  );
}
